/**
 * CR-7105: Row-level cells for GRNScoreChart's drill-down table, moved out of
 * GRNScoreChart.tsx (500-line cap) unchanged except for the new View Appeal
 * affordance. appealStatusCell + RowActions originate from CR-4648.
 * CR-7319: Appeal / Update Appeal carry the gavel — the warning triangle
 * read as a status marker rather than an action.
 */
import { type ReactElement } from 'react';
import { X, Gavel, CheckCircle, Eye } from 'lucide-react';
import type { GoodsReceiptNote } from '../../../lib/api/types/grn-types';
import { isQuantityOnlyAppeal } from '../tabs/delivery/appealEligibility';

/** CR-4648: Mirror DeliveryTab.getAppealStatusBadge so the drill-down's
 * Appeal Status column matches the parent list's labelling exactly.
 */
export function appealStatusCell(grn: GoodsReceiptNote): ReactElement {
  const latest = grn.latest_appeal;
  const onTime = grn.on_time_status;
  const wf = grn.workflow_status;
  const pill = (label: string, cls: string) => (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${cls}`}>{label}</span>
  );
  if (!latest) {
    if (wf === 'acknowledged') return pill('Acknowledged', 'bg-green-100 text-green-800');
    if (onTime === 'on_time') return <span className="text-gray-400">-</span>;
    return pill('Not Appealed', 'bg-gray-100 text-gray-500');
  }
  switch (latest.status) {
    case 'awaiting_response':
      return pill(
        latest.is_customer_appeal ? 'Awaiting Supplier Response' : 'Awaiting Customer Response',
        'bg-yellow-100 text-yellow-800'
      );
    case 'accepted':
      return pill('Accepted', 'bg-green-100 text-green-800');
    case 'queried':
      return pill('Query – Awaiting Supplier Response', 'bg-blue-100 text-blue-800');
    case 'rejected':
      return pill('Rejected', 'bg-red-100 text-red-800');
    case 'cancelled':
      return pill('Appeal Cancelled', 'bg-gray-200 text-gray-600');
  }
}

export interface RowActionsProps {
  grn: GoodsReceiptNote;
  companyType: string | undefined;
  busy: boolean;
  hideAppealForSupplier: boolean;
  onAcknowledge: (grn: GoodsReceiptNote) => void;
  onAppeal: (grn: GoodsReceiptNote) => void;
  onCancelAppeal: (grn: GoodsReceiptNote) => void;
  onRespondAppeal: (grn: GoodsReceiptNote) => void;
  onViewAppeal: (grn: GoodsReceiptNote) => void;
}

/** CR-4648: Per-row action cluster. Mirrors the gating logic in
 * DeliveryDetailsModal's footer but scoped to a single GRN row so the
 * drill-down can act on each sibling delivery directly.
 * CR-7105: + View Appeal — any row carrying an appeal offers a read-only
 * view of its content (the raiser previously had only Cancel; a resolved or
 * cancelled appeal had nothing at all). Suppressed while a Respond button is
 * shown (the respond modal already renders the same content), and hidden for
 * suppliers under the same CR-5884 toggle as the other appeal controls. */
export function RowActions({
  grn, companyType, busy, hideAppealForSupplier,
  onAcknowledge, onAppeal, onCancelAppeal, onRespondAppeal, onViewAppeal,
}: RowActionsProps) {
  const latest = grn.latest_appeal;
  const isOnTime = grn.on_time_status === 'on_time';
  const isEarlyOrLate = grn.on_time_status === 'early' || grn.on_time_status === 'late';
  const isPendingReview = grn.workflow_status === 'pending_review';
  const isAcknowledged = grn.workflow_status === 'acknowledged';
  // On time with the wrong quantity: Appeal (on quantity) only, never Acknowledge.
  const quantityOnly = isQuantityOnlyAppeal(grn) && grn.can_appeal === true;

  const dash = <span className="text-gray-400">-</span>;
  if (isOnTime && !latest && !quantityOnly) return dash;
  // CR-7105: an acknowledged row with a cancelled appeal previously returned
  // a dash here; it now falls through so the cancelled appeal stays viewable.
  if (isAcknowledged && !latest) return dash;

  const btnBase = 'px-2 py-1 rounded text-xs font-medium flex items-center gap-1 disabled:opacity-50';

  const viewAppealButton = (
    <button
      onClick={() => onViewAppeal(grn)} disabled={busy}
      className={`${btnBase} bg-white border border-gray-300 text-gray-700 hover:bg-gray-50`}
    >
      <Eye className="h-3.5 w-3.5" /> View Appeal
    </button>
  );

  if (companyType === 'supplier') {
    const ownActiveAppeal = !!latest && !latest.is_customer_appeal
      && (latest.status === 'awaiting_response' || latest.status === 'queried');
    const canAcknowledge = isPendingReview && isEarlyOrLate && !ownActiveAppeal;
    const canAppeal = !hideAppealForSupplier && !!grn.can_appeal
      && (canAcknowledge || (quantityOnly && isPendingReview && !ownActiveAppeal));
    const canUpdateAppeal = !hideAppealForSupplier && latest?.status === 'queried' && !latest.is_customer_appeal;
    const canCancelAppeal = !hideAppealForSupplier && ownActiveAppeal;
    const canViewAppeal = !hideAppealForSupplier && !!latest;
    // CR-4648 rework: fall back to a dash whenever no branch fires, rather
    // than leaning on a hand-maintained inverse condition. With the CR-5884
    // hide-toggle on, a queried row suppressed every appeal control and left
    // the Action cell blank.
    if (!canAcknowledge && !canAppeal && !canUpdateAppeal && !canCancelAppeal && !canViewAppeal) return dash;
    return (
      <div className="flex flex-wrap gap-2">
        {/* Pre-appeal: Acknowledge + Appeal side by side */}
        {canAcknowledge && (
          <button
            onClick={() => onAcknowledge(grn)} disabled={busy}
            className={`${btnBase} bg-green-600 text-white hover:bg-green-700`}
          >
            <CheckCircle className="h-3.5 w-3.5" /> Acknowledge
          </button>
        )}
        {canAppeal && (
          <button
            onClick={() => onAppeal(grn)} disabled={busy}
            className={`${btnBase} bg-orange-600 text-white hover:bg-orange-700`}
          >
            <Gavel className="h-3.5 w-3.5" /> Appeal
          </button>
        )}
        {/* Queried by customer → Update Appeal */}
        {canUpdateAppeal && (
          <button
            onClick={() => onAppeal(grn)} disabled={busy}
            className={`${btnBase} bg-orange-600 text-white hover:bg-orange-700`}
          >
            <Gavel className="h-3.5 w-3.5" /> Update Appeal
          </button>
        )}
        {/* Active supplier-raised appeal → Cancel */}
        {canCancelAppeal && (
          <button
            onClick={() => onCancelAppeal(grn)} disabled={busy}
            className={`${btnBase} bg-gray-500 text-white hover:bg-gray-600`}
          >
            <X className="h-3.5 w-3.5" /> Cancel Appeal
          </button>
        )}
        {canViewAppeal && viewAppealButton}
      </div>
    );
  }

  if (companyType === 'customer' || companyType === 'helix') {
    // Mirrors the customer/helix footer cluster in DeliveryDetailsModal.
    // hasPendingFromSupplier = a *supplier-raised* appeal awaiting our
    // response. hasActiveAppeal = any in-flight appeal from either side —
    // Acknowledge is suppressed while one is open. hasResolvedAppeal =
    // accepted/rejected, i.e. the row is closed to further action.
    const hasPendingFromSupplier = latest?.status === 'awaiting_response' && !latest.is_customer_appeal;
    const hasActiveAppeal = !!latest && (latest.status === 'awaiting_response' || latest.status === 'queried');
    const hasResolvedAppeal = !!latest && (latest.status === 'rejected' || latest.status === 'accepted');
    // CR-6313: the customer-side Appeal button is back, mirroring the
    // Delivery Details footer. CR-4648 removed it on 2026-07-08 because the
    // backend 403'd customer-side creation with "Only the supplier can
    // appeal a delivery", so every click was a guaranteed error. That block
    // has been reverted — Sarah confirmed on 2026-09-08 that a customer
    // captures an appeal on the supplier's behalf and it is accepted on
    // submission — so the click now succeeds and this branch must offer it
    // again, or the drill-down silently disagrees with the modal footer.
    const open = !hasActiveAppeal && !hasResolvedAppeal && isPendingReview;
    const canAcknowledge = open && isEarlyOrLate;
    const canAppealAsCustomer = open && (isEarlyOrLate || quantityOnly) && !!grn.can_appeal;
    const canViewAppeal = !!latest && !hasPendingFromSupplier;
    if (!hasPendingFromSupplier && !canAcknowledge && !canAppealAsCustomer && !canViewAppeal) return dash;
    return (
      <div className="flex flex-wrap gap-2">
        {hasPendingFromSupplier && (
          <button
            onClick={() => onRespondAppeal(grn)} disabled={busy}
            className={`${btnBase} bg-blue-600 text-white hover:bg-blue-700`}
          >
            <CheckCircle className="h-3.5 w-3.5" /> Respond to Appeal
          </button>
        )}
        {canAcknowledge && (
          <button
            onClick={() => onAcknowledge(grn)} disabled={busy}
            className={`${btnBase} bg-green-600 text-white hover:bg-green-700`}
          >
            <CheckCircle className="h-3.5 w-3.5" /> Acknowledge
          </button>
        )}
        {canAppealAsCustomer && (
          <button
            onClick={() => onAppeal(grn)} disabled={busy}
            className={`${btnBase} bg-orange-600 text-white hover:bg-orange-700`}
          >
            <Gavel className="h-3.5 w-3.5" /> Appeal
          </button>
        )}
        {canViewAppeal && viewAppealButton}
      </div>
    );
  }

  return dash;
}
